import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, TrendingUp, TrendingDown, Wallet, Ticket, PoundSterling, Trophy, Download, Hourglass, AlertTriangle } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { toast } from "../../hooks/use-toast";
import { PromoPerformance } from "./PromoPerformance";
import { UpgradeLock } from "../../components/UpgradeLock";

const statusBadge = {
  active: "bg-green-100 text-green-700",
  part_used: "bg-amber-100 text-amber-700",
  used: "bg-red-100 text-red-600",
};

function buildMonthlySeries(vouchers) {
  // Last 6 months revenue by purchase month
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleString("en-GB", { month: "short" }), revenue: 0 });
  }
  const idx = Object.fromEntries(months.map((m, i) => [m.key, i]));
  vouchers.forEach((v) => {
    if (!v.created_at) return;
    const d = new Date(v.created_at);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key in idx) months[idx[key]].revenue += v.original_amount || 0;
  });
  return months.map((m) => ({ ...m, revenue: Math.round(m.revenue * 100) / 100 }));
}

function buildTopSellers(vouchers) {
  const map = {};
  vouchers.forEach((v) => {
    const t = v.product_title || "Untitled";
    if (!map[t]) map[t] = { title: t, count: 0, revenue: 0 };
    map[t].count += 1;
    map[t].revenue += v.original_amount || 0;
  });
  return Object.values(map)
    .map((m) => ({ ...m, revenue: Math.round(m.revenue * 100) / 100 }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);
}

const BAR_COLORS = ["#2ec9c9", "#4bd4b0", "#69c07a", "#f4b740", "#f47c7c"];

function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function SalesTab() {
  const [data, setData] = useState(null);
  const [pct, setPct] = useState(5);
  const [breakage, setBreakage] = useState(null);
  useEffect(() => {
    api.get("/business/vouchers").then(({ data }) => setData(data));
    api.get("/business/payout").then(({ data }) => setPct(data.commission_pct)).catch(() => {});
    api.get("/business/breakage").then(({ data }) => setBreakage(data)).catch(() => {});
  }, []);
  if (!data) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const series = buildMonthlySeries(data.vouchers);
  const topSellers = buildTopSellers(data.vouchers);
  const thisMonth = series[series.length - 1]?.revenue || 0;
  const lastMonth = series[series.length - 2]?.revenue || 0;
  const momGrowth = lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth) * 100 : (thisMonth > 0 ? 100 : 0);
  const momUp = momGrowth >= 0;

  const exportCsv = () => {
    const rows = [["GiftVouchers.app — Sales report"], ["Generated", new Date().toLocaleString("en-GB")], []];
    rows.push(["Monthly revenue (last 6 months)"]);
    rows.push(["Month", "Revenue (£)"]);
    series.forEach((m) => rows.push([m.label, m.revenue.toFixed(2)]));
    rows.push(["Month-over-month growth", `${momGrowth.toFixed(1)}%`]);
    rows.push([]);
    rows.push(["Top-selling vouchers"]);
    rows.push(["Rank", "Voucher", "Units sold", "Revenue (£)"]);
    topSellers.forEach((t, i) => rows.push([i + 1, t.title, t.count, t.revenue.toFixed(2)]));
    downloadCsv("giftvouchers-sales-report.csv", rows);
  };

  const exportBreakage = async () => {
    try {
      const { data: rep } = await api.get("/business/breakage/report");
      const rows = [["GiftVouchers.app — Breakage & liability report"], ["Generated", new Date().toLocaleString("en-GB")], []];
      if (breakage) {
        rows.push(["Active outstanding (£)", breakage.active_outstanding.toFixed(2), `${breakage.active_count} voucher(s)`]);
        rows.push(["Expired breakage (£)", breakage.breakage_value.toFixed(2), `${breakage.breakage_count} voucher(s)`]);
        rows.push(["Total unredeemed (£)", breakage.total_unredeemed.toFixed(2)]);
        rows.push(["Issued (£)", breakage.issued_value.toFixed(2), "Redeemed (£)", breakage.redeemed_value.toFixed(2)]);
        rows.push([]);
      }
      rows.push(["Code", "Voucher", "Buyer email", "Original (£)", "Redeemed (£)", "Balance (£)", "Status", "Category", "Issued", "Expires"]);
      rep.rows.forEach((r) => rows.push([r.code, r.product, r.buyer_email, r.original_amount.toFixed(2), r.redeemed.toFixed(2), r.balance.toFixed(2), r.status, r.category, r.issued, r.expires]));
      downloadCsv("giftvouchers-breakage-report.csv", rows);
      toast({ title: "Report downloaded", description: `${rep.count} unredeemed voucher(s) exported.` });
    } catch {
      toast({ title: "Export failed", description: "Could not generate the report", variant: "destructive" });
    }
  };

  const cards = [
    { label: "Total sales", value: `£${data.stats.total_sales.toFixed(2)}`, icon: PoundSterling },
    { label: "Vouchers sold", value: data.stats.count, icon: Ticket },
    { label: "Redeemed", value: `£${data.stats.redeemed.toFixed(2)}`, icon: TrendingUp },
    { label: "Outstanding", value: `£${data.stats.outstanding.toFixed(2)}`, icon: Wallet },
  ];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={exportCsv} data-testid="export-analytics-csv" className="gp-btn-outline rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2">
          <Download size={16} /> Export report (CSV)
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
            <div className="w-10 h-10 rounded-xl bg-[#eefafa] flex items-center justify-center mb-3">
              <c.icon className="text-[#2ec9c9]" size={20} />
            </div>
            <p className="text-[#8a97a8] text-sm">{c.label}</p>
            <p className="text-2xl font-bold text-[#16243b] mt-1">{c.value}</p>
          </div>
        ))}
      </div>

      <UpgradeLock feature="promo_performance"><PromoPerformance /></UpgradeLock>

      {/* Breakage & liability */}
      {breakage && (
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mb-8" data-testid="breakage-card">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <Wallet className="text-[#2ec9c9]" size={18} />
              <h3 className="font-semibold text-[#16243b]">Outstanding liability &amp; breakage</h3>
            </div>
            <button onClick={exportBreakage} data-testid="breakage-export-btn" className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 shrink-0"><Download size={14} /> Download report (CSV)</button>
          </div>
          <p className="text-xs text-[#8a97a8] mb-4">The value of vouchers you've sold that customers haven't redeemed yet — your outstanding liability, plus expired balances (breakage) you're likely to keep.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="rounded-xl p-5 bg-[#eefafa]">
              <div className="flex items-center gap-2 text-[#5a6b80] text-sm"><Hourglass size={15} /> Active outstanding</div>
              <p className="text-2xl font-bold text-[#16243b] mt-1" data-testid="breakage-active">£{breakage.active_outstanding.toFixed(2)}</p>
              <p className="text-xs text-[#8a97a8] mt-1">{breakage.active_count} valid voucher(s) — future redemption liability</p>
            </div>
            <div className="rounded-xl p-5 bg-[#fff6e9]">
              <div className="flex items-center gap-2 text-[#5a6b80] text-sm"><AlertTriangle size={15} /> Expired (breakage)</div>
              <p className="text-2xl font-bold text-[#16243b] mt-1" data-testid="breakage-expired">£{breakage.breakage_value.toFixed(2)}</p>
              <p className="text-xs text-[#8a97a8] mt-1">{breakage.breakage_count} expired voucher(s) — likely retained revenue</p>
            </div>
            <div className="rounded-xl p-5 bg-[#f6f8fb]">
              <div className="flex items-center gap-2 text-[#5a6b80] text-sm"><Wallet size={15} /> Total unredeemed</div>
              <p className="text-2xl font-bold text-[#16243b] mt-1" data-testid="breakage-total">£{breakage.total_unredeemed.toFixed(2)}</p>
              <p className="text-xs text-[#8a97a8] mt-1">of £{breakage.issued_value.toFixed(2)} issued · £{breakage.redeemed_value.toFixed(2)} redeemed</p>
            </div>
          </div>
        </div>
      )}

      {/* Revenue chart */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mb-8" data-testid="revenue-chart-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-[#16243b]">Revenue over time</h3>
            <span data-testid="mom-growth-badge" className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${momUp ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
              {momUp ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
              {momUp ? "+" : ""}{momGrowth.toFixed(1)}% MoM
            </span>
          </div>
          <span className="text-xs text-[#8a97a8]">Last 6 months</span>
        </div>
        <p className="text-xs text-[#8a97a8] -mt-2 mb-4">
          This month £{thisMonth.toFixed(2)} vs last month £{lastMonth.toFixed(2)}
        </p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2ec9c9" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2ec9c9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#8a97a8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#8a97a8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(val) => [`£${Number(val).toFixed(2)}`, "Revenue"]} contentStyle={{ borderRadius: 12, border: "1px solid #eef1f5" }} />
              <Area type="monotone" dataKey="revenue" stroke="#2ec9c9" strokeWidth={3} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top-selling vouchers */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mb-8" data-testid="top-sellers-card">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="text-[#f4b740]" size={18} />
          <h3 className="font-semibold text-[#16243b]">Top-selling vouchers</h3>
        </div>
        {topSellers.length === 0 ? (
          <p className="py-8 text-center text-[#8a97a8]">No sales yet.</p>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6 items-center">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topSellers} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#8a97a8", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="title" width={110} tick={{ fill: "#5a6b80", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(val) => [`£${Number(val).toFixed(2)}`, "Revenue"]} contentStyle={{ borderRadius: 12, border: "1px solid #eef1f5" }} />
                  <Bar dataKey="revenue" radius={[0, 8, 8, 0]}>
                    {topSellers.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {topSellers.map((t, i) => (
                <div key={t.title} className="flex items-center justify-between p-3 rounded-xl bg-[#f6f8fb]" data-testid="top-seller-row">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ background: BAR_COLORS[i % BAR_COLORS.length] }}>{i + 1}</span>
                    <span className="font-medium text-[#16243b] truncate">{t.title}</span>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="font-bold text-[#16243b] text-sm">£{t.revenue.toFixed(2)}</p>
                    <p className="text-xs text-[#8a97a8]">{t.count} sold</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
